import { faInfoCircle, faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';

const EmailBoxEditModal = ({ showModal, data, handleClose, handleSubmit }) => {
  const [body, setBody] = useState({
    name: '',
    email: '',
    password: '',
    provider: '',
    imapHost: '',
    imapPort: '',
    sendingEnabled: false,
    migratedToGraph: false,
    smtpHost: '',
    smtpPort: '',
    status: ''
  });
  const [validated, setValidated] = useState(false);
  const form = useRef(null);
  const isAuthorized = data.status !== 'UNAUTHORIZED';
  const [showPermissionWarning, setShowPermissionWarning] = useState(false);

  useEffect(() => {
    if (
      body.provider === 'OFFICE_365' &&
      body.sendingEnabled &&
      !body.migratedToGraph
    ) {
      setShowPermissionWarning(true);
    } else {
      setShowPermissionWarning(false);
    }
  }, [body.sendingEnabled, body.migratedToGraph]);

  useEffect(() => {
    if (Object.keys(data).length !== 0) {
      setBody({
        ...data,
        imapHost: data.imapHost || data.imap || '',
        imapPort: data.imapPort || data.port || '',
        sendingEnabled: data.sendingEnabled || false,
        smtpHost: data.smtpHost || '',
        smtpPort: data.smtpPort || ''
      });
      setValidated(false);
    }
  }, [data]);

  useEffect(() => {
    if (showModal) {
      setBody({
        ...data,
        imapHost: data.imapHost || data.imap || '',
        imapPort: data.imapPort || data.port || '',
        sendingEnabled: data.sendingEnabled || false,
        smtpHost: data.smtpHost || '',
        smtpPort: data.smtpPort || ''
      });
      setValidated(false);
    }
  }, [showModal]);

  const onSubmit = e => {
    if (form.current.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      handleSubmit({
        name: body.name.trim(),
        email: body.email.trim(),
        password: body.password ? body.password.trim() : null,
        provider: body.provider.trim(),
        imapHost: body.imapHost.trim(),
        imapPort: Number(body.imapPort),
        sendingEnabled: body.sendingEnabled,
        smtpHost: body.smtpHost ? body.smtpHost.trim() : null,
        smtpPort: body.smtpPort ? Number(body.smtpPort) : null,
        status: body.status
      });
      setValidated(false);
    }
  };

  const handleChangeProvider = e => {
    switch (e.target.value) {
      case 'OFFICE_365':
        setBody(prevBody => ({
          ...prevBody,
          provider: e.target.value,
          imapHost: 'outlook.office365.com',
          imapPort: '993',
          password: null
        }));
        break;
      case 'GOOGLE':
        setBody(prevBody => ({
          ...prevBody,
          provider: e.target.value,
          imapHost: 'imap.gmail.com',
          imapPort: '993',
          password: null
        }));
        break;
      default:
        setBody(prevBody => ({
          ...prevBody,
          provider: e.target.value,
          imapHost: '',
          imapPort: ''
        }));
    }
  };

  return (
    <>
      <Modal show={showModal} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar caixa de email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form validated={validated} ref={form}>
            <Row className="mb-3">
              <Form.Group as={Col} md="6" controlId="emailbox.name">
                <Form.Label>
                  Nome <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="Nome"
                  value={body.name}
                  onChange={e =>
                    setBody(prevBody => ({ ...prevBody, name: e.target.value }))
                  }
                />
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Insira um nome.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md="6" controlId="emailbox.provider">
                <Form.Label>
                  Provedor <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  aria-label="Provedor"
                  className="flex-1 me-2"
                  onChange={handleChangeProvider}
                  value={body.provider}
                  required
                >
                  <option value="">Selecione uma opção</option>
                  <option value="OFFICE_365">Office 365</option>
                  <option value="GOOGLE">Google</option>
                  <option value="OTHERS">Outros</option>
                </Form.Select>
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Insira um provedor.
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md="6" controlId="emailbox.email">
                <Form.Label>
                  E-mail <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="email"
                  placeholder="E-mail"
                  value={body.email}
                  onChange={e =>
                    setBody(prevBody => ({
                      ...prevBody,
                      email: e.target.value
                    }))
                  }
                />
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Insira um e-mail.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md="6" controlId="emailbox.password">
                <Form.Label>Senha</Form.Label>
                <Form.Control
                  required={body.provider == 'OTHERS'}
                  disabled={body.provider != 'OTHERS' && body.provider != ''}
                  type="password"
                  placeholder="Senha"
                  value={body.password}
                  onChange={e =>
                    setBody(prevBody => ({
                      ...prevBody,
                      password: e.target.value
                    }))
                  }
                />
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Insira uma senha.
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group
                as={Col}
                md={isAuthorized ? 6 : 10}
                controlId="emailbox.imapHost"
              >
                <Form.Label>
                  IMAP Host <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="IMAP Host"
                  value={body.imapHost}
                  onChange={e =>
                    setBody(prevBody => ({
                      ...prevBody,
                      imapHost: e.target.value
                    }))
                  }
                />
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Insira o endereço IMAP.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md="2" controlId="emailbox.imapPort">
                <Form.Label>
                  Porta IMAP <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="Porta"
                  value={body.imapPort}
                  onChange={e =>
                    setBody(prevBody => ({
                      ...prevBody,
                      imapPort: e.target.value
                    }))
                  }
                />
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Insira uma porta.
                </Form.Control.Feedback>
              </Form.Group>

              {isAuthorized && (
                <Form.Group as={Col} md="4" controlId="emailbox.status">
                  <Form.Label>
                    Status <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Check
                    type="switch"
                    id="status-switch"
                    label={body.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                    checked={body.status === 'ACTIVE'}
                    onChange={() =>
                      setBody(prevBody => ({
                        ...prevBody,
                        status:
                          prevBody.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
                      }))
                    }
                  />
                  <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    Escolha uma opção.
                  </Form.Control.Feedback>
                </Form.Group>
              )}
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md="12" controlId="emailbox.sendingEnabled">
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    checked={body.sendingEnabled}
                    onChange={e =>
                      setBody(prevBody => ({
                        ...prevBody,
                        sendingEnabled: e.target.checked
                      }))
                    }
                    className="m-0 me-2"
                  />
                  <Form.Label className="m-0 me-1">
                    Habilitar envio de e-mails
                  </Form.Label>
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id="tooltip-sending-enabled">
                        Para provedores Office 365 e Google, não é necessário
                        preencher as configurações manuais de SMTP. O sistema
                        utiliza integração moderna via API (Microsoft Graph e
                        Google API) para garantir maior segurança e estabilidade
                        no envio.
                      </Tooltip>
                    }
                  >
                    <span>
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-secondary cursor-pointer"
                        size="sm"
                      />
                    </span>
                  </OverlayTrigger>
                </div>
              </Form.Group>
            </Row>

            {showPermissionWarning && (
              <Alert variant="warning" className="mb-3">
                Será enviado um e-mail de confirmação para{' '}
                <code>
                  <b>{body.email}</b>
                </code>{' '}
                para habilitar o envio de e-mails.
              </Alert>
            )}

            {body.sendingEnabled && (
              <Row className="mb-3">
                <Form.Group as={Col} md="10" controlId="emailbox.smtpHost">
                  <Form.Label>SMTP Host</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="SMTP Host"
                    value={body.smtpHost}
                    onChange={e =>
                      setBody(prevBody => ({
                        ...prevBody,
                        smtpHost: e.target.value
                      }))
                    }
                  />
                </Form.Group>

                <Form.Group as={Col} md="2" controlId="emailbox.smtpPort">
                  <Form.Label>Porta SMTP</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Porta"
                    value={body.smtpPort}
                    onChange={e =>
                      setBody(prevBody => ({
                        ...prevBody,
                        smtpPort: e.target.value
                      }))
                    }
                  />
                </Form.Group>
              </Row>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="falcon-default" onClick={handleClose}>
            Fechar
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EmailBoxEditModal;
